import { ApiError, ProblemType } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { oversightApi } from './api/oversight-api';
import type { IResponseRecycled, ResponseStatus } from './api/oversight-contracts';
import { neverArrived, refusalMessage } from './refusal';

/**
 * The recycle bin in words (SR-470, over survey-api's recycle bin design): staff move a draft,
 * submitted or locked response to the bin — its advisor no longer sees it, and nothing is deleted
 * — and restore it to the status it left. One module, so the record page's survey strip, the
 * response page, the bin's list and Lists say each thing once and alike: the presses, what a move
 * does, where a recycled response sits, and each refusal. The status chip keeps the wire's word,
 * `recycled`; the place is the "Recycle bin".
 *
 * survey-api judges every move. This module decides only which press a read status is offered
 * (`LIVE` → the move, `recycled` → the restore, `archived` → neither), and says a refusal by its
 * `type`, never by its `title`. Rejected: `refusalMessage` alone, as most admin writes use — it
 * would say survey-api's English title for `live-slot-taken` with nobody named and no way to the
 * response that holds the slot, and a read gone stale would read like a refusal that no re-read
 * can change.
 *
 * **Many at once is one call per response, in turn** (`runMoves`) — the bin list's restore and
 * Lists' move of its ticked rows: survey-api has no bulk route (its design §4), so each response
 * is sent at its own revision, and a refusal is that response's alone. The bin list's rows are
 * the responses' and carry it; a Lists row's `revision` is the row's own, so a Lists move reads
 * its response just before sending (the owner's Q8 (a)) — refused then for its status, never for
 * an answer changed since the list's read.
 */

/** The statuses that hold a participant's slot in a cycle — survey-api's live set, kept to one
 *  response per cycle by its index `ux_response_live_per_cycle`. */
export const LIVE: ReadonlySet<ResponseStatus> = new Set<ResponseStatus>([
  'draft',
  'submitted',
  'locked'
]);

/** survey-api's bound on a move's reason, after trimming — said by the dialog, never enforced. */
export const RECYCLE_REASON_MAX = 2_000;

export type RecycleMove = 'recycle' | 'restore';

/** The move a press offers on a response of this status, or none (an archived response). */
export function moveOf(status: ResponseStatus): Nullable<RecycleMove> {
  if (status === 'recycled') return 'restore';
  return LIVE.has(status) ? 'recycle' : null;
}

/** Each move in words: the press, its dialog's title and confirm, the start of a refusal, the
 *  move named in a sentence, what it did to a response, and a run's count of them. */
export const RECYCLE_WORDS: Record<
  RecycleMove,
  {
    press: string;
    title: string;
    confirm: string;
    not: string;
    what: string;
    past: string;
    done: string;
  }
> = {
  recycle: {
    press: 'Move to recycle bin…',
    title: 'Move this response to the recycle bin?',
    confirm: 'Move to recycle bin',
    not: 'Not moved',
    what: 'the move to the recycle bin',
    past: 'moved',
    done: 'moved to the recycle bin'
  },
  restore: {
    press: 'Restore…',
    title: 'Restore this response from the recycle bin?',
    confirm: 'Restore',
    not: 'Not restored',
    what: 'the restore',
    past: 'restored',
    done: 'restored'
  }
};

/** Sends one move — survey-api's `recycle` or `restore` route, at the revision `etag` quotes. */
export function sendMove(move: RecycleMove, responseId: string, etag: string, reason: string) {
  return (move === 'recycle' ? oversightApi.recycle : oversightApi.restore)(
    responseId,
    etag,
    reason
  );
}

/** What a landed move says — naming the response, since the record page's strip may show another
 *  one of the survey once the page is read again; `status` is the status the move left it in. */
export function landedSentence(subject: IRecycleSubject, status: ResponseStatus): string {
  const which = `the ${subject.survey} response${subject.cycle ? ` of ${subject.cycle}` : ''}`;
  return status === 'recycled'
    ? `Moved ${which} to the recycle bin.`
    : `Restored ${which} as ${status}.`;
}

/** What a move to the bin does, said in its dialog: what the advisor loses, then what stays. */
export const RECYCLE_SAYS =
  'Its advisor no longer sees this response and cannot open it. Nothing is deleted: its ' +
  'answers, flags and notes stay, and a restore brings it back as it was.';

/** What a restore does, said in its dialog — `from` the status the response left, where the read
 *  carried it. */
export function restoreSays(from: IResponseRecycled['from'] | undefined): string {
  const as = from ? `as ${from}` : 'to the status it left';
  return `The response returns ${as}, and its advisor sees it again.`;
}

/**
 * Where a recycled response sits, as one sentence: since when and moved by whom — with the status
 * it left where `withFrom` (the response page, whose Answers offer no correction until a
 * restore). A row of the legacy platform's bin has neither date nor name, and says so; a read
 * from an API that carries no `recycled` says only where the response is.
 */
export function inTheBin(
  recycled: Nullable<IResponseRecycled> | undefined,
  withFrom = false
): string {
  if (!recycled) return 'In the recycle bin.';
  const from = withFrom ? ` from ${recycled.from}` : '';
  if (recycled.at === null) {
    return `Moved to the recycle bin${from} on the legacy platform, which kept no date or name.`;
  }
  const by = recycled.by ? ` by ${recycled.by.email}` : '';
  const moved = from || by ? `, moved${from}${by}` : '';
  return `In the recycle bin since ${formatDateTime(recycled.at)}${moved}.`;
}

/** Whose response a refusal is about — the names `live-slot-taken`'s sentence says. */
export interface IRecycleSubject {
  participant: string;
  survey: string;
  /** Absent where the list does not carry it: a Lists row is one per participant and survey. */
  cycle?: string;
}

/** Whose a response is, as a row of the responses list, the hub or the detail names it. */
export function subjectOf(response: {
  participant: { displayName: string };
  survey: { name: string };
  cycle: { name: string };
}): IRecycleSubject {
  return {
    participant: response.participant.displayName,
    survey: response.survey.name,
    cycle: response.cycle.name
  };
}

/**
 * A refused move, and where it is said:
 *
 * - `dialog`: in the dialog, which stays open with the reason as typed — a 400 (the reason's
 *   length), the policy's 403, a missing precondition: pressing again may help, and nothing on the
 *   page is out of date;
 * - `page`: the dialog closes and the sentence is said beside the press — with `reread` where the
 *   page's read is out of date (the status moved, the revision moved, the response went, or no
 *   answer arrived), and `holder` on `live-slot-taken`, the live response to open ("Open it").
 */
export type RecycleRefusal =
  | { kind: 'dialog'; sentence: string }
  | { kind: 'page'; sentence: string; reread: boolean; holder: Nullable<string> };

export function recycleRefusal(
  error: unknown,
  move: RecycleMove,
  subject: IRecycleSubject
): RecycleRefusal {
  const not = RECYCLE_WORDS[move].not;
  const page = (sentence: string, reread: boolean, holder: Nullable<string> = null) =>
    ({ kind: 'page', sentence, reread, holder }) as const;

  // An answer that never arrived is not a refusal: the move may have landed.
  if (!arrived(error)) {
    return page(
      neverArrived(
        RECYCLE_WORDS[move].what,
        'The response is read again — check its status before pressing again.'
      ),
      true
    );
  }

  if (error.isProblem(ProblemType.transitionRefused)) {
    // The status rides in an extension named `status` (`IProblemDetails.status`).
    const status = error.problem?.status;
    const now = typeof status === 'string' ? status : null;
    if (move === 'recycle') {
      return page(
        now
          ? `${not}: the response is already ${now}.`
          : `${not}: the response’s status no longer allows it.`,
        true
      );
    }
    return page(
      `${not}: the response is no longer in the recycle bin${now ? ` — it is ${now}` : ''}.`,
      true
    );
  }

  if (error.isProblem(ProblemType.liveSlotTaken)) {
    return page(
      `${not}: ${subject.participant} already has a live ${subject.survey} response in ` +
        `${subject.cycle ?? 'its cycle'}.`,
      false,
      error.problem?.responseId ?? null
    );
  }

  if (error.isProblem(ProblemType.revisionConflict)) {
    return page(`${not}: the response changed after this page read it.`, true);
  }

  if (error.status === 404) return page(`${not}: the response is no longer there.`, true);

  if (error.isProblem(ProblemType.preconditionRequired)) {
    return {
      kind: 'dialog',
      sentence:
        'This page sent the move without the response’s revision — a fault in the page, not in ' +
        'the response. Reload and try again.'
    };
  }

  // The policy's bodiless 403: `responses.annotate` is gone (the forbidden handler re-reads the
  // session, and the press is drawn off on the next render).
  if (error.status === 403) {
    return {
      kind: 'dialog',
      sentence:
        `${not}: your Team Portal roles no longer include annotating responses, which a move ` +
        'to the recycle bin asks.'
    };
  }

  return { kind: 'dialog', sentence: refusalMessage(error, `${not}: it was refused.`) };
}

/** survey-api's answer arrived: an `ApiError` under 500. Anything else never arrived, and the move
 *  may have landed. */
function arrived(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status < 500;
}

/** One response of a run over a list's ticked rows, and whose it is. */
export interface IRecycleTarget {
  responseId: string;
  /** The response's revision as the list read it, or `null` where the list's revision is not the
   *  response's (a Lists row's is the row's own) — the response is then read just before its
   *  move, and sent at that revision. */
  revision: Nullable<number>;
  subject: IRecycleSubject;
}

/** A response a run's move was refused for, in `recycleRefusal`'s words. */
export interface IRecycleRowRefusal {
  subject: IRecycleSubject;
  sentence: string;
  /** `live-slot-taken`'s holder, opened by "Open it". */
  holder: Nullable<string>;
}

/**
 * What a run came back with. `stopped` is where it ended early: at a refusal every response would
 * meet (`dialog` — the reason's length, the policy, the precondition), at a read before a move that
 * got no answer, or at a move whose answer never arrived (`unknown` — it may have landed), with the
 * `unsent` responses after that one.
 */
export interface IRecycleRun {
  move: RecycleMove;
  landed: number;
  refused: IRecycleRowRefusal[];
  stopped: Nullable<{ sentence: string; unsent: number; dialog: boolean; unknown: boolean }>;
}

/**
 * One move over many responses, one call after another, each with `If-Match: "<revision>"` — the
 * list's, else the response's `ETag` read just before (`IRecycleTarget.revision`). A refusal that
 * is the response's own — its status or revision moved, it is gone, its participant's slot is
 * taken — is kept and the run goes on, since the next response is another's. A refusal every
 * response would meet, or an answer that never arrived, stops it: sending on would only repeat it,
 * or pile writes on a connection that is down. The read before a move is judged apart from the
 * move (the owner's Q9): one that fails sent nothing, so it is said as not moved, never as a move
 * whose landing is not known, and its 403 is the read's policy, not the move's.
 */
export async function runMoves(
  move: RecycleMove,
  targets: readonly IRecycleTarget[],
  reason: string
): Promise<IRecycleRun> {
  const words = RECYCLE_WORDS[move];
  const run: IRecycleRun = { move, landed: 0, refused: [], stopped: null };
  const stop = (sentence: string, unsent: number, dialog = false, unknown = false) => {
    run.stopped = { sentence, unsent, dialog, unknown };
    return run;
  };
  // A refusal that is the response's own is kept, and the run goes on (`true`); one every
  // response would meet stops it.
  const refuse = (error: ApiError, subject: IRecycleSubject, unsent: number): boolean => {
    const refusal = recycleRefusal(error, move, subject);
    if (refusal.kind === 'dialog') {
      stop(refusal.sentence, unsent, true);
      return false;
    }
    run.refused.push({ subject, sentence: refusal.sentence, holder: refusal.holder });
    return true;
  };

  for (const [index, target] of targets.entries()) {
    const unsent = targets.length - index - 1;
    const { subject } = target;
    let etag: string;
    try {
      etag = await etagOf(target);
    } catch (error) {
      if (!arrived(error)) {
        return stop(
          `${subject.participant}’s response could not be read, so it was not ${words.past}.`,
          unsent
        );
      }
      if (error.status === 403) {
        return stop(
          `${words.not}: your Team Portal roles no longer include browsing responses, which the ` +
            'read before the move asks.',
          unsent,
          true
        );
      }
      if (refuse(error, subject, unsent)) continue;
      return run;
    }
    try {
      await sendMove(move, target.responseId, etag, reason);
      run.landed += 1;
    } catch (error) {
      if (!arrived(error)) {
        const what = `${words.what} of ${subject.participant}’s response`;
        return stop(
          neverArrived(what, 'The list is read again — check it before pressing again.'),
          unsent,
          false,
          true
        );
      }
      if (!refuse(error, subject, unsent)) return run;
    }
  }
  return run;
}

/** What a target's move quotes: the revision its list read, else the response's own, read now. */
async function etagOf({ responseId, revision }: IRecycleTarget): Promise<string> {
  if (revision !== null) return `"${revision}"`;
  const { body, etag } = await oversightApi.getResponse(responseId);
  return etag ?? `"${body.revision}"`;
}

/** What the run's dialog says and stays open for: a refusal every response would meet, met by
 *  the first — nothing was moved, and the reason is kept for the press again. */
export function runRefusedInDialog(run: IRecycleRun): Nullable<string> {
  return run.landed === 0 && run.refused.length === 0 && run.stopped?.dialog
    ? run.stopped.sentence
    : null;
}

/** A run's dialog in words, over `count` responses. */
export function runWords(
  move: RecycleMove,
  count: number
): { title: string; says: string; confirm: string } {
  const many = count === 1 ? '1 response' : `${count} responses`;
  const inTurn =
    ' They are sent one at a time: one that is refused is named, and the rest still go.';
  return move === 'recycle'
    ? {
        title: `Move ${many} to the recycle bin?`,
        says:
          'Their advisors no longer see these responses and cannot open them. Nothing is ' +
          'deleted: their answers, flags and notes stay, and a restore brings each back as it ' +
          `was.${inTurn}`,
        confirm: RECYCLE_WORDS.recycle.confirm
      }
    : {
        title: `Restore ${many} from the recycle bin?`,
        says: `Each returns as the status it left, and its advisor sees it again.${inTurn}`,
        confirm: RECYCLE_WORDS.restore.confirm
      };
}

/** A run's outcome: "3 restored." — "None …" where nothing landed — and a move whose answer never
 *  arrived counted apart, since it may have landed (the owner's Q9): "2 restored, 1 not known.",
 *  or "1 not known." alone. */
export function runHeadline(run: IRecycleRun): string {
  const { done } = RECYCLE_WORDS[run.move];
  if (run.stopped?.unknown) {
    return run.landed === 0 ? '1 not known.' : `${run.landed} ${done}, 1 not known.`;
  }
  return `${run.landed === 0 ? 'None' : run.landed} ${done}.`;
}

/** Where a run stopped, and what it did not send. */
export function runStoppedSentence(stopped: NonNullable<IRecycleRun['stopped']>): string {
  if (stopped.unsent === 0) return stopped.sentence;
  const rest = stopped.unsent === 1 ? '1 more was' : `${stopped.unsent} more were`;
  return `${stopped.sentence} The run stopped there: ${rest} not sent.`;
}

/** Which response a refusal is about — a participant may hold several in a list. */
export function runRefusalWhose({ subject }: IRecycleRowRefusal): string {
  const cycle = subject.cycle ? `, ${subject.cycle}` : '';
  return `${subject.participant} (${subject.survey}${cycle})`;
}

/** A run's outcome as one line of text, for a list that says its answers in a status line. */
export function runSaid(run: IRecycleRun): string {
  return [
    runHeadline(run),
    ...run.refused.map((refused) => `${runRefusalWhose(refused)} — ${refused.sentence}`),
    run.stopped ? runStoppedSentence(run.stopped) : null
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
}

/** How many of the recycle bin's responses the record search `q` reaches, within `cycle` where
 *  named (SR-490) — the top search's offer and the search results page's card. */
export async function countRecycled(
  q: string,
  cycle?: string,
  signal?: AbortSignal
): Promise<number> {
  const page = await oversightApi.listResponses(
    { q, cycleCode: cycle || undefined, status: ['recycled'], pageSize: 1 },
    signal
  );
  return page.total;
}
